// Priorities — #331 stage 2.
//
// WHAT THIS SURFACES, AND WHY THE REASON IS NOT OPTIONAL
// =====================================================
// `GET /api/me/ea/priorities` ranks the owner's open commitments against the
// goals they have CONFIRMED, and `goals.score_item` attaches a sentence to
// every item explaining its position. The card asks for "a ranked priority
// list with reasons", and the reason is the half that makes it trustworthy: a
// list of things in an order nobody can account for is an opinion wearing a
// number. So `priority_reason` is rendered on every row, never behind a
// disclosure, and never summarised away.
//
// `goals_considered` is rendered too. With no confirmed goals the backend
// still ranks — by deadline and commitment — and says so per item. The header
// says it once, plainly, rather than letting the owner assume the order
// reflects priorities they never set.
//
// NOTHING HERE IS INVENTED
// ========================
// Every field comes off the response. An item with no due date shows no due
// date rather than a plausible one, and the score is shown as the backend
// computed it. R6: if a surface has no data behind it, render the designed
// empty state.
import React from "react";

import EmptyState from "../../components/EmptyState";
import PanelError from "./PanelError";
import { ENDPOINTS } from "./api";
import usePanelData from "./usePanelData";

/** A due date as written, or nothing. Never a guess.
 *
 *  The backend stores whatever was captured, which is not guaranteed to be an
 *  ISO timestamp. A value that does not parse is shown verbatim rather than
 *  silently dropped or rendered as "Invalid Date".
 */
export function dueLabel(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

/** The one-line account of why the list is in this order. */
export function rankingBasis(goalsConsidered) {
  const n = Number(goalsConsidered) || 0;
  if (n > 0) {
    return `Ranked against ${n} confirmed goal${n === 1 ? "" : "s"}.`;
  }
  return "No goals confirmed yet, so this is ranked by deadline and commitment alone.";
}

export default function Priorities() {
  const { status, data, error } = usePanelData(ENDPOINTS.priorities);

  if (status === "loading") {
    return <div className="ea-panel-loading dim" role="status">Reading your priorities…</div>;
  }
  if (status === "disabled") {
    return (
      <EmptyState
        title="Not enabled"
        detail="Your assistant is not switched on for this account."
      />
    );
  }
  if (status === "denied") {
    return (
      <EmptyState
        title="Not yours to see"
        detail="Priorities belong to the account that owns the assistant."
      />
    );
  }
  if (status === "error") {
    return <PanelError error={error} />;
  }

  const items = (data && data.priorities) || [];

  if (!items.length) {
    return (
      <EmptyState
        title="Nothing is ranked yet"
        detail={
          "This ranks the commitments your assistant has captured. She has not " +
          "captured any yet — tell her what you have promised and it will appear here."
        }
      />
    );
  }

  return (
    <div className="ea-panel">
      <p className="ea-panel-basis dim">{rankingBasis(data && data.goals_considered)}</p>

      <ol className="ea-prio-list">
        {items.map((item, i) => {
          const due = dueLabel(item.due_date);
          return (
            <li className="ea-prio" key={item.commitment_id || `${item.title}-${i}`}>
              <span className="ea-prio-rank" aria-hidden="true">{i + 1}</span>
              <div className="ea-prio-body">
                <div className="ea-prio-title">{item.title || "Untitled"}</div>
                {/* The reason is the point of this screen. */}
                <div className="ea-prio-reason">{item.priority_reason}</div>
                <div className="ea-prio-meta dim">
                  {item.kind ? <span className="ea-tag">{item.kind}</span> : null}
                  {due ? <span>Due {due}</span> : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
